import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useState, useEffect } from "react";
import Sidebar from "@/components/Sidebar";
import Login from "@/pages/Login";
import Home from "@/pages/Home";
import AudienceIntelligence from "@/pages/AudienceIntelligence";
import PriorityAccounts from "@/pages/PriorityAccounts";
import AccountIntelligence from "@/pages/AccountIntelligence";
import CampaignDashboard from "@/pages/CampaignDashboard";
import LeadsManagement from "@/pages/LeadsManagement";
import Analytics from "@/pages/Analytics";
import { dataApi } from "@/lib/api";
import { Loader2, Database } from "lucide-react";

function ProtectedRoute({ children }) {
  const token = localStorage.getItem("oaa_token");
  if (!token) return <Navigate to="/login" replace />;
  return children;
}

function CacheLoadingBanner() {
  const [status, setStatus] = useState({ loading: true, entries: 0, ready: false });
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    let interval;
    const check = async () => {
      try {
        const res = await dataApi.getCacheStatus();
        setStatus(res.data);
        if (res.data.ready && !res.data.loading) {
          setVisible(false);
          clearInterval(interval);
        }
      } catch {}
    };
    check();
    interval = setInterval(check, 4000);
    return () => clearInterval(interval);
  }, []);

  if (!visible) return null;

  return (
    <div data-testid="cache-loading-banner"
      className="bg-[#F66922]/8 border border-[#F66922]/15 rounded-lg px-4 py-2.5 mb-4 flex items-center gap-3">
      <Loader2 className="w-4 h-4 text-[#F66922] animate-spin" />
      <div className="flex-1">
        <p className="text-sm font-medium text-[#F66922]">
          Loading data from database...
        </p>
        <p className="text-xs text-slate-500">
          {status.entries > 0
            ? `${status.entries} datasets cached. Pages will load faster as more data is pre-loaded.`
            : 'First load takes a moment. After that, all pages load instantly from cache.'}
        </p>
      </div>
      <Database className="w-4 h-4 text-[#F66922]/40" />
    </div>
  );
}

function AppLayout({ children }) {
  return (
    <div className="flex min-h-screen bg-white">
      <Sidebar />
      <main className="flex-1 ml-[220px] p-8 lg:p-10 max-w-[1600px]">
        <CacheLoadingBanner />
        {children}
      </main>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/audience-intelligence" element={<AudienceIntelligence />} />
                  <Route path="/priority-accounts" element={<PriorityAccounts />} />
                  <Route path="/account/:id" element={<AccountIntelligence />} />
                  <Route path="/campaigns" element={<CampaignDashboard />} />
                  <Route path="/leads" element={<LeadsManagement />} />
                  <Route path="/analytics" element={<Analytics />} />
                </Routes>
              </AppLayout>
            </ProtectedRoute>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
